import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  IconButton,
  Tooltip,
  Collapse,
  List,
  ListItem,
  ListItemText,
  Chip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import EditIcon from "@mui/icons-material/Edit";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";
import scriptRunnerV2Service from "../../services/scriptRunnerV2.service";
import ConfirmationDialog from "../../components/confirmationdialog";
import useScriptPermissions from "./hooks/useScriptPermissions";

// interns scriptrunner - Row component for the scripts table with collapsible versions
const ScriptRow = ({ script, onRefresh }) => {
  const navigate = useNavigate(); // interns scriptrunner - added navigate hook here
  const { canWrite } = useScriptPermissions();
  const [open, setOpen] = useState(false);
  const [versions, setVersions] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);

  // interns scriptrunner - State for deletion confirmation
  const [confirmDelete, setConfirmDelete] = useState({
    open: false,
    type: null,
    data: null,
  });

  const handleToggle = async () => {
    setOpen(!open);
    if (!open && versions.length === 0) {
      await fetchVersions();
    }
  };

  const fetchVersions = async () => {
    setLoadingVersions(true);
    try {
      const data = await scriptRunnerV2Service.getScriptVersions(script._id);
      // interns scriptrunner - Sort versions semantically (descending)
      const sortedData = [...data].sort((a, b) => {
        const pa = a.version.split(".").map(Number);
        const pb = b.version.split(".").map(Number);
        if (pa[0] !== pb[0]) return pb[0] - pa[0];
        if (pa[1] !== pb[1]) return pb[1] - pa[1];
        return (pb[2] || 0) - (pa[2] || 0);
      });
      setVersions(sortedData);
    } catch (error) {
      console.error("interns scriptrunner - Failed to fetch versions", error);
    } finally {
      setLoadingVersions(false);
    }
  };

  const handleDeleteClick = (e, type, data) => {
    e.currentTarget.blur();
    setConfirmDelete({ open: true, type, data });
  };

  const handleConfirmDelete = async () => {
    const { type, data } = confirmDelete;
    try {
      if (type === "script") {
        await scriptRunnerV2Service.deleteScript(data._id);
        onRefresh();
      } else if (type === "version") {
        await scriptRunnerV2Service.deleteScriptVersion(script._id, data._id);
        // interns scriptrunner - Always refresh parent to handle parent soft-deletion or lastVersion updates
        await onRefresh();
        // If the script still exists in the parent list, the row will re-render.
        // We can also refresh the local versions list if the row is still open.
        if (open) {
          await fetchVersions();
        }
      }
    } catch (error) {
      console.error(`interns scriptrunner - Failed to delete ${type}`, error);
      alert(`Failed to delete ${type}. Please try again.`);
    } finally {
      setConfirmDelete({ open: false, type: null, data: null });
    }
  };

  return (
    <React.Fragment>
      <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
        <TableCell>
          <IconButton size="small" onClick={handleToggle}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row">
          <Typography sx={{ fontWeight: "bold" }}>{script.name}</Typography>
        </TableCell>
        <TableCell>{script.description}</TableCell>
        <TableCell>
          <Chip
            label={script.lastVersion || "N/A"}
            size="small"
            color="primary"
            variant="outlined"
          />
        </TableCell>
        <TableCell>{new Date(script.created).toLocaleDateString()}</TableCell>
        <TableCell>{script.createdBy}</TableCell>
        {/* <TableCell align="right">
          {canWrite && (
            <Tooltip title="Delete Script">
              <IconButton
                size="small"
                color="error"
                onClick={(e) => handleDeleteClick(e, "script", script)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </TableCell> */}
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2, bgcolor: "#f5f5f5", p: 2, borderRadius: 2 }}>
              <Typography
                variant="subtitle2"
                gutterBottom
                component="div"
                sx={{ fontWeight: "bold" }}
              >
                Script Versions
              </Typography>
              {loadingVersions ? (
                <CircularProgress size={24} sx={{ m: 2 }} />
              ) : versions.length > 0 ? (
                <List dense>
                  {versions.map((v) => (
                    <ListItem key={v._id} divider>
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: "bold" }}
                            >
                              v{v.version}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              Entrypoint: {v.entrypoint}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ fontStyle: "italic" }}
                            >
                              "{v.changeSummary}"
                            </Typography>
                          </Box>
                        }
                        secondary={`Uploaded on ${new Date(v.created).toLocaleString()} by ${v.createdBy}`}
                      />
                      {/* interns scriptrunner - Run button to open execution page */}
                      <Tooltip title="Run Script">
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() =>
                            navigate(
                              `/script-runner-V2/execute/${script._id}/${v.version}`,
                            )
                          }
                        >
                          <PlayArrowIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      {/* interns scriptrunner - Edit button to open workspace */}
                      {canWrite && (
                        <Tooltip title="Edit Code">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() =>
                              navigate(
                                `/script-runner-V2/edit/${script._id}/${v.version}`,
                              )
                            }
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canWrite && (
                        <Tooltip title="Delete Version">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => handleDeleteClick(e, "version", v)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="S3 Path">
                        <Typography
                          variant="caption"
                          sx={{ color: "#1976d2", cursor: "help" }}
                        >
                          S3 Metadata
                        </Typography>
                      </Tooltip>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No versions found.
                </Typography>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      {/* Deletion Confirmation Dialog */}
      <ConfirmationDialog
        open={confirmDelete.open}
        handleClose={() =>
          setConfirmDelete({ open: false, type: null, data: null })
        }
        handleConfirm={handleConfirmDelete}
        title={`Confirm Delete ${confirmDelete.type === "script" ? "Script" : "Version"}`}
        message={`Are you sure you want to delete this ${confirmDelete.type === "script" ? "script and all its versions" : "version"}? This action cannot be undone.`}
      />
    </React.Fragment>
  );
};

// interns scriptrunner - Main component for viewing all scripts (V2)
const ViewScriptsV2 = () => {
  const navigate = useNavigate();
  const { canWrite } = useScriptPermissions();
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchScripts();
  }, []);

  const fetchScripts = async () => {
    try {
      setLoading(true);
      const data = await scriptRunnerV2Service.getAllScripts();
      setScripts(data);
      setError(null);
    } catch (err) {
      console.error("interns scriptrunner - Failed to fetch scripts", err);
      // interns scriptrunner - Log the specific error message from the backend
      if (err.response && err.response.data) {
        console.log(
          "interns scriptrunner - Backend Error Details:",
          err.response.data,
        );
      }
      setError(
        "Failed to load scripts. Please check your connection or permissions.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 4 }}>
      {/* interns scriptrunner - Navigation header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
          position: "relative", // needed for absolute back button
        }}
      >
        {/* Back Button */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/script-runner-V2")}
          sx={{
            position: "absolute",
            left: 0,
            color: "#28BCBB",
            backgroundColor: "#f0f9f9",
            border: "1px solid rgba(40, 188, 187, 0.3)",
            boxShadow: "0 2px 6px rgba(40, 188, 187, 0.15)",
            borderRadius: "8px",
            padding: "6px 14px",
            fontWeight: 600,
            textTransform: "none",
            transition: "all 0.3s ease",
            "&:hover": {
              backgroundColor: "#e6f0f1",
              borderColor: "#188182",
              boxShadow: "0 4px 12px rgba(40, 188, 187, 0.25)",
            },
          }}
        >
          Back to Dashboard
        </Button>

        {/* Upload Button */}
        {canWrite && (
          <Button
            onClick={() => navigate("/script-runner-V2/upload")}
            sx={{
              marginLeft: "auto", // keeps it right aligned
              backgroundColor: "#1d9f9f",
              color: "#fff",
              fontWeight: 600,
              textTransform: "none",
              borderRadius: "8px",
              padding: "8px 18px",
              boxShadow: "0 2px 6px rgba(29, 159, 159, 0.25)",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: "#188182",
                boxShadow: "0 4px 10px rgba(29, 159, 159, 0.3)",
              },
              "&:active": {
                backgroundColor: "#167172",
                boxShadow: "0 1px 4px rgba(29, 159, 159, 0.2)",
              },
            }}
          >
            Upload New Script
          </Button>
        )}
      </Box>

      <Typography
        variant="h4"
        gutterBottom
        sx={{ fontWeight: "bold", color: "#1d9f9f" }}
      >
        Available Scripts (V2)
      </Typography>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Paper sx={{ p: 3, bgcolor: "#fff0f0", border: "1px solid #ffcccc" }}>
          <Typography color="error">{error}</Typography>
          <Button onClick={fetchScripts} sx={{ mt: 2 }}>
            Retry
          </Button>
        </Paper>
      ) : scripts.length === 0 ? (
        <Paper sx={{ p: 10, textAlign: "center", borderRadius: 3 }}>
          <Typography variant="h6" color="textSecondary">
            No scripts found.
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Be the first to upload an automation script for the interns project!
          </Typography>
          {canWrite && (
            <Button
              variant="outlined"
              onClick={() => navigate("/script-runner-V2/upload")}
            >
              Go to Upload
            </Button>
          )}
        </Paper>
      ) : (
        <TableContainer
          component={Paper}
          elevation={3}
          sx={{ borderRadius: 3 }}
        >
          <Table aria-label="scripts table">
            <TableHead sx={{ bgcolor: "#f5f5f5" }}>
              <TableRow>
                <TableCell width="50" />
                <TableCell>
                  <Typography sx={{ fontWeight: "bold" }}>
                    Script Name
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography sx={{ fontWeight: "bold" }}>
                    Description
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography sx={{ fontWeight: "bold" }}>
                    Latest Version
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography sx={{ fontWeight: "bold" }}>
                    Created At
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography sx={{ fontWeight: "bold" }}>Author</Typography>
                </TableCell>
                {/* <TableCell align="right">
                  <Typography sx={{ fontWeight: "bold" }}>Actions</Typography>
                </TableCell> */}
              </TableRow>
            </TableHead>
            <TableBody>
              {scripts.map((script) => (
                <ScriptRow
                  key={script._id}
                  script={script}
                  onRefresh={fetchScripts}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default ViewScriptsV2;
